import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { Send, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { RevealText } from "@/components/shared/RevealText";
import { SectionReveal } from "@/components/shared/SectionReveal";
import apiClient from "@/lib/api";

export default function Contact() {
  const { t } = useTranslation();
  const { lang = "fr" } = useParams();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post("/contact", { ...form, lang });
      setSent(true);
      toast.success(t("contact.form_success"));
    } catch {
      toast.error(t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO title={`${t("contact.hero_title")} — Fastority`} description={t("contact.hero_subtitle")} path="contact" />

      <section className="bg-deepblue text-white py-24 sm:py-28 grain-overlay relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 relative">
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            <RevealText lines={[t("contact.hero_title")]} />
          </h1>
          <p className="mt-6 text-lg text-white/70">{t("contact.hero_subtitle")}</p>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-offwhite">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          {sent ? (
            <SectionReveal className="text-center bg-emeraldgreen/10 border border-emeraldgreen/30 rounded-3xl p-10">
              <CheckCircle2 className="mx-auto mb-3 text-emeraldgreen" size={36} />
              <p className="font-heading font-bold text-lg text-slate900" data-testid="contact-form-success">{t("contact.form_success")}</p>
            </SectionReveal>
          ) : (
            <SectionReveal>
              <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-3xl p-8 border border-gray-100 shadow-[0_8px_30px_rgba(30,58,138,0.06)] space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required data-testid="contact-form-name" placeholder={t("common.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
                  <input required type="email" data-testid="contact-form-email" placeholder={t("common.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
                </div>
                <input required data-testid="contact-form-subject" placeholder={t("common.subject")} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
                <textarea required data-testid="contact-form-message" placeholder={t("common.message")} rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-deepblue" />
                <button type="submit" disabled={loading} data-testid="contact-form-submit" className="w-full inline-flex items-center justify-center gap-2 bg-coral text-white font-semibold px-6 py-4 rounded-full hover:scale-[1.01] transition-transform duration-300 disabled:opacity-60">
                  {loading ? t("common.sending") : t("common.send")} <Send size={16} />
                </button>
              </form>
            </SectionReveal>
          )}

          <SectionReveal delay={0.1} className="mt-10 text-center">
            <p className="text-sm text-slate-500 mb-3">{t("contact.info_title")}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to={`/${lang}/press`} data-testid="contact-press-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-deepblue hover:text-coral transition-colors">
                <Mail size={14} /> {t("contact.press_link")}
              </Link>
              <Link to={`/${lang}/pro`} data-testid="contact-pro-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-deepblue hover:text-coral transition-colors">
                <Mail size={14} /> {t("contact.pro_link")}
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
